import * as React from 'react';
import OlMap from 'ol/Map';
import OlLayerImage from 'ol/layer/Image';
import OlImageSource from 'ol/source/Image';
import OlLayerTile from 'ol/layer/Tile';
import OlTileSource from 'ol/source/Tile';
import OlSourceImageWMS from 'ol/source/ImageWMS';
import OlSourceTileWMS from 'ol/source/TileWMS';
import OlSourceWMTS, { Options } from 'ol/source/WMTS';
import { MapComponent } from '@terrestris/react-geo';
import '../css/MyLayerSwitcher.css';

import _isEqual from 'lodash/isEqual'; // const _isEqual = require('lodash/isEqual');

/**
 * @export
 * @interface LayerSwitcherProps
 * @extends {React.HTMLAttributes<HTMLDivElement>}
 */
export interface BaseProps {
    /**
     * The layers to be available in the switcher.
     */
    layers: Array<OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>>;

    /**
     * The main map the layers should be synced with.
     */
    map: OlMap;

    /**
     * Callback to inform of the baseLayer selected
     */
    onBaseLayerChanged?: (layerSelected: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>) => void;
    
    /**
     * Index del layer dins el mapa que s'intercambiarà
     */
     layerindex?: number;
}

interface LayerSwitcherState {
    previewLayer: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>;
}

export type LayerSwitcherProps = BaseProps & React.HTMLAttributes<HTMLDivElement>;

/**
 * Class representing the LayerSwitcher.
 * A basic component to switch between the passed layers.
 * This is most likely to be used for the backgroundlayer.
 *
 * @class LayerSwitcher
 * @extends React.Component
 */
export class MyLayerSwitcher extends React.Component<LayerSwitcherProps, LayerSwitcherState> {

    /**
     * The internal map of the LayerSwitcher
     */
    _map: OlMap = null;

    /**
     * Index del Layer que està visible en el mapa gran
     */
    _visibleLayerIndex = 0;

    /**
     * Index del Layer que es posarà visible en el mapa gran
     */
     _newVisibleLayerIndex = 0;

     /**
     * Llista de capes copiades
     */
    _layerClones: Array<OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>> = [];

    /**
     * The className of this component.
     */
    static readonly _className = 'react-geo-layer-switcher';

    /**
     * Per defecte el Layer que intercambiem és el 0
     */
     static defaultProps = {
        layerindex: 0
    };

    /**
     * Creates the LayerSwitcher.
     *
     * @constructs LayerSwitcher
     */
    constructor(props: LayerSwitcherProps) {
        super(props);
        this._map = this.getMap();
        this.state = {
            previewLayer: null
        };
    }

    /**
     * A react lifecycle method called when the component did mount.
     */
    componentDidMount() {
        this.setMapLayers();
        this.updateLayerVisibility();
    }

    /**
     * Destroy all map specific stuff when umounting the component.
     */
    componentWillUnmount() {
        this._map.getLayers().clear();
        this._map.setTarget(null);
        this._map = null;
    }

    /**
     * Invoked immediately after updating occurs. This method is not called for
     * the initial render.
     *
     * @param prevProps The previous props.
     */
    componentDidUpdate(prevProps: LayerSwitcherProps) {
        if (!(_isEqual(this.props.layers, prevProps.layers))) {
            this.setMapLayers();
            this.updateLayerVisibility();
        }
    }

    cloneOlSourceImageWMS(source: OlSourceImageWMS): OlSourceImageWMS {
        return new OlSourceImageWMS({
            url: source.getUrl(), 
            params: source.getParams(), 
            projection: source.getProjection()
        });
    }

    cloneOlSourceTileWMS(source: OlSourceTileWMS): OlSourceTileWMS {
        return new OlSourceTileWMS({
            url: source.getUrls()[0],
            params: source.getParams(), 
            projection: source.getProjection()
        });
    }
    
    cloneOlSourceWMTS(cap: Options): OlSourceWMTS {
        return new OlSourceWMTS(cap);
    }
    
    /**
     * Clones a layer
     *
     * @param layer The layer to clone.
     * @returns The cloned layer.
     */
    cloneLayer = (layer: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>): OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource> => {
        let layerClone: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>;
        if (layer.get('singleTile')) {
            layerClone = new OlLayerImage<OlImageSource>({
                ...layer.getProperties(),
                source: this.cloneOlSourceImageWMS(((layer as OlLayerImage<OlImageSource>).getSource() as OlSourceImageWMS)),
                // properties: {
                //     originalLayer: layer
                // },
            });
        } else {
            layerClone = new OlLayerTile<OlTileSource>({
                ...layer.getProperties(),
                source: layer.get('cap') ? this.cloneOlSourceWMTS(layer.get('cap')) : this.cloneOlSourceTileWMS(((layer as OlLayerTile<OlTileSource>).getSource() as OlSourceTileWMS)),
                // properties: {
                //     originalLayer: layer
                // },
            });
        }
        // layerClone.set('originalLayer', layer);
        return layerClone;
    }

    /**
     * (Re-)adds the layers to the preview map and sets the visibleLayerIndex.
     */
    setMapLayers = () => {
        const {
            layers
        } = this.props;
        if (layers.length < 2) {
            console.log(`LayerSwitcher requires two or more layers.`);
        }
        console.log('SETMAP (I) -> Length:', this._layerClones.length, 'LayerIndex:', this._visibleLayerIndex, 'NewLayerIndex:', this._newVisibleLayerIndex);
        this._map.getLayers().clear();
        this._visibleLayerIndex = -1;
        this._layerClones = layers.map((layer, index) => {
            const layerClone = this.cloneLayer(layer);
            if (this._visibleLayerIndex < 0) {
                if (layerClone.getVisible()) {
                    this._visibleLayerIndex = index;
                    this._newVisibleLayerIndex = index;// Després de fer un seMapLayer farem un updateLayerVisibility i no volem que es faci cap update
                }
            } else {
                layerClone.setVisible(false);
            }
            return layerClone;
        });
        if (this._visibleLayerIndex < 0) {
            this._visibleLayerIndex = 0;
            this._newVisibleLayerIndex = 0;// Després de fer un seMapLayer farem un updateLayerVisibility i no volem que es faci cap update
            
        } else {
            this._layerClones[this._visibleLayerIndex].setVisible(false);
        }
        let nextLayerIndex = this._visibleLayerIndex + 1;
        if (nextLayerIndex === this._layerClones.length) {
            nextLayerIndex = 0;
        }
        this._layerClones[nextLayerIndex].setVisible(true);
        this._map.addLayer(this._layerClones[nextLayerIndex]);
        console.log('SETMAP (F) -> Length:', this._layerClones.length, 'LayerIndex:', this._visibleLayerIndex, 'NewLayerIndex:', this._newVisibleLayerIndex);
    }

    /**
     * Sets the visiblity of the layers in the props.map and this._map.
     * Also sets the previewLayer in the state.
     */
    updateLayerVisibility = () => {
        const {
            map,
            layers,
            layerindex
        } = this.props;
        console.log('UPDATE (I) -> Length:', this._layerClones.length, 'LayerIndex:', this._visibleLayerIndex, 'NewLayerIndex:', this._newVisibleLayerIndex);
        if (this._visibleLayerIndex !== this._newVisibleLayerIndex) {
            layers.forEach((l, i) => {
                if (this._newVisibleLayerIndex === i) {
                    l.setVisible(true);
                    map.getLayers().insertAt(layerindex, l);
                } else {
                    l.setVisible(false);
                }
            });
            map.getLayers().removeAt(layerindex + 1);
            this._visibleLayerIndex = this._newVisibleLayerIndex;
            this._layerClones[this._newVisibleLayerIndex].setVisible(false);
            let nextLayerIndex = this._newVisibleLayerIndex + 1;
            if (nextLayerIndex === this._layerClones.length) {
                nextLayerIndex = 0;
            }
            this._layerClones[nextLayerIndex].setVisible(true);
            this._map.getLayers().insertAt(0, this._layerClones[nextLayerIndex]);
            this._map.getLayers().removeAt(1);
        }
        console.log('UPDATE (F) -> Length:', this._layerClones.length, 'LayerIndex:', this._visibleLayerIndex, 'NewLayerIndex:', this._newVisibleLayerIndex);
    }

    /**
     * Constructs this._map
     */
    getMap = (): OlMap => {
        const {
            map
        } = this.props;
        return new OlMap({
            view: map.getView(),
            controls: []
        });
    }

    /**
     * Clickhandler for the overview switch.
     */
    onSwitcherClick = (evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        console.log('CLICK (I) -> Length:', this._layerClones.length, 'LayerIndex:', this._visibleLayerIndex, 'NewLayerIndex:', this._newVisibleLayerIndex);
        evt.stopPropagation();
        this._layerClones.forEach((layer, index: number) => {
            if (layer.getVisible()) {
                this._newVisibleLayerIndex = index;
            }
        });
        this.updateLayerVisibility();
        if (this.props.onBaseLayerChanged) {
            const {
                layers
            } = this.props;
            this.props.onBaseLayerChanged(layers[this._visibleLayerIndex]);
        }
        console.log('CLICK (F) -> Length:', this._layerClones.length, 'LayerIndex:', this._visibleLayerIndex, 'NewLayerIndex:', this._newVisibleLayerIndex);
    }

    /**
     * The render function.
     */
    render() {
        const {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onBaseLayerChanged,
            ...passThroughProps
        } = this.props;

        return (
            <div
                className={MyLayerSwitcher._className}
                {...passThroughProps}
            >
                <div
                    className="clip"
                    onClick={this.onSwitcherClick}
                >
                    <MapComponent
                        className="lsm"
                        mapDivId="layer-switcher-map"
                        map={this._map}
                    />
                    {
                        this.state.previewLayer &&
                        <span className="layer-title">
                            {this.state.previewLayer.get('name')}
                        </span>
                    }
                </div>
            </div>
        );
    }
}

export default MyLayerSwitcher;